/**
 * V280: 근로일정 템플릿 엔진
 *
 * 확장 가능한 구조:
 * - templateId → TemplateDefinition
 * - generateSchedule(year, month) → 월별 요일별 스케줄 자동 생성
 *
 * 현재 지원:
 * - KAKAO_PC: 카카오게임즈 PC 근무 (월~금 요일별 차등 + 14일 간격 놀금)
 */

import { DailySchedule } from '../types/workSchedule';

// ===== 놀금(Play Friday) 판정 =====

/**
 * 놀금 기준일: 2026-04-10 (금요일)
 * 이후 14일 간격으로 반복되는 금요일만 놀금.
 *
 * 판정: (D - ANCHOR) 의 일수 차이가 14의 배수면 놀금
 * ※ 월 기준 순번("2번째/4번째 금요일") 사용 절대 금지
 */
const PLAY_FRIDAY_ANCHOR = new Date(2026, 3, 10); // 2026-04-10 (month 0-indexed)

export function isPlayFriday(date: Date): boolean {
  // 금요일이 아니면 false
  if (date.getDay() !== 5) return false;

  // UTC 기준 일수 차이 계산 (로컬 시간대 보정)
  const anchor = new Date(PLAY_FRIDAY_ANCHOR.getFullYear(), PLAY_FRIDAY_ANCHOR.getMonth(), PLAY_FRIDAY_ANCHOR.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffMs = target.getTime() - anchor.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  return diffDays % 14 === 0;
}

// ===== 타입 정의 =====

/** 하루 스케줄 + 휴무 여부 */
export interface TemplateDaySchedule {
  date: string;        // "YYYY-MM-DD"
  dayOfWeek: number;   // 0=Sun ... 6=Sat
  isWeekend: boolean;
  isPlayFriday: boolean;
  isOff: boolean;      // 놀금 또는 주말 → true
  schedule: DailySchedule | null; // null = off/주말
}

/** 템플릿이 생성한 월별 결과 */
export interface TemplateGenerationResult {
  templateId: string;
  templateName: string;
  /** 화~목 기본 스케줄 (base 신청용) */
  base: DailySchedule;
  /** 전체 평일 날짜별 상세 (미리보기/변경 신청용) */
  days: TemplateDaySchedule[];
  /** base 와 다른 날짜들 (변경 신청 필요) */
  overrides: TemplateDaySchedule[];
  /** 놀금 날짜 목록 */
  playFridays: string[];
}

/** 템플릿 정의 */
export interface WorkScheduleTemplate {
  id: string;
  name: string;
  description: string;
  /** 대상 companyCode (null = 모든 회사) */
  companyCode: 'KR' | 'CN' | null;
  generate: (year: number, month: number) => TemplateGenerationResult;
}

// ===== 카카오게임즈 PC 템플릿 =====

const KAKAO_PC_SCHEDULES: Record<number, DailySchedule> = {
  // dayOfWeek: 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri
  1: { startTime: '10:30', lunchStart: '12:00', lunchEnd: '13:00', endTime: '19:00' }, // 월요일
  2: { startTime: '10:00', lunchStart: '12:00', lunchEnd: '13:00', endTime: '19:00' }, // 화요일
  3: { startTime: '10:00', lunchStart: '12:00', lunchEnd: '13:00', endTime: '19:00' }, // 수요일
  4: { startTime: '10:00', lunchStart: '12:00', lunchEnd: '13:00', endTime: '19:00' }, // 목요일
  5: { startTime: '10:00', lunchStart: '12:00', lunchEnd: '13:00', endTime: '17:30' }, // 금요일 (일반)
};

/** 화~목 기본 = base 신청용 */
const KAKAO_PC_BASE: DailySchedule = KAKAO_PC_SCHEDULES[2]; // 화요일 = 기본

function generateKakaoPcSchedule(year: number, month: number): TemplateGenerationResult {
  const days: TemplateDaySchedule[] = [];
  const overrides: TemplateDaySchedule[] = [];
  const playFridays: string[] = [];

  // 1일부터 말일까지 순회
  const daysInMonth = new Date(year, month, 0).getDate(); // month is 1-indexed here
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month - 1, d);
    const dayOfWeek = date.getDay();
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const playFri = isPlayFriday(date);
    const isOff = isWeekend || playFri;

    let schedule: DailySchedule | null = null;
    if (!isWeekend && !playFri) {
      schedule = KAKAO_PC_SCHEDULES[dayOfWeek] || KAKAO_PC_BASE;
    }

    const entry: TemplateDaySchedule = {
      date: dateStr,
      dayOfWeek,
      isWeekend,
      isPlayFriday: playFri,
      isOff,
      schedule,
    };

    days.push(entry);

    if (playFri) {
      playFridays.push(dateStr);
    }

    // base(화~목 10:00-19:00) 와 다른 평일 = override 필요
    if (!isWeekend && !playFri && schedule) {
      const isBase =
        schedule.startTime === KAKAO_PC_BASE.startTime &&
        schedule.lunchStart === KAKAO_PC_BASE.lunchStart &&
        schedule.lunchEnd === KAKAO_PC_BASE.lunchEnd &&
        schedule.endTime === KAKAO_PC_BASE.endTime;
      if (!isBase) {
        overrides.push(entry);
      }
    }
  }

  return {
    templateId: 'KAKAO_PC',
    templateName: '카카오게임즈 PC',
    base: KAKAO_PC_BASE,
    days,
    overrides,
    playFridays,
  };
}

// ===== 템플릿 레지스트리 =====

export const WORK_SCHEDULE_TEMPLATES: WorkScheduleTemplate[] = [
  {
    id: 'KAKAO_PC',
    name: '카카오게임즈 PC',
    description: '월 10:30~19:00 / 화~목 10:00~19:00 / 금 10:00~17:30 / 격주 놀금',
    companyCode: 'KR',
    generate: generateKakaoPcSchedule,
  },
  // 향후 템플릿 추가 시 여기에 등록
];

/**
 * companyCode 에 맞는 템플릿 목록 반환
 * @param companyCode 사용자 companyCode (null/undefined = 전체)
 */
export function getTemplatesForCompany(companyCode?: string | null): WorkScheduleTemplate[] {
  if (!companyCode) return [];
  return WORK_SCHEDULE_TEMPLATES.filter(
    (t) => t.companyCode === null || t.companyCode === companyCode
  );
}

// ===== 요일 한글 라벨 =====

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
export function getDayLabel(dayOfWeek: number): string {
  return DAY_LABELS[dayOfWeek] || '';
}
